'use client';

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import store from 'src/redux/store';
import { useDebounce } from 'use-debounce';
import { useGetOpenPaymentsCategorySuggestionsInfiniteQuery } from 'src/api/redux';
import {
  GenericNodeComponent,
  GenericSelectorModal,
  ConnectorType,
} from '../shared';
import {
  OpenPaymentsCategoryItem,
  openPaymentsCategoryNodeConfig,
  openPaymentsCategoryModalConfig,
  transformOpenPaymentsCategory,
} from './config';

interface OpenPaymentsCategoryComponentProps {
  componentId: string;
  initialOpenPaymentsCategory?: OpenPaymentsCategoryItem[];
  initialConnector?: ConnectorType;
  onUpdate: (componentId: string, data: any) => void;
  onRemove: (componentId: string) => void;
  fromPlaceholder?: boolean;
}

const OpenPaymentsCategoryComponent: React.FC<
  OpenPaymentsCategoryComponentProps
> = ({
  componentId,
  initialOpenPaymentsCategory,
  onUpdate,
  onRemove,
  fromPlaceholder = false,
}) => {
  // Initialize state - ui_metadata provides pre-hydrated objects
  // Handle legacy case where initialOpenPaymentsCategory might contain string IDs
  const getInitialCategories = (): OpenPaymentsCategoryItem[] => {
    if (
      !initialOpenPaymentsCategory ||
      initialOpenPaymentsCategory.length === 0
    )
      return [];
    const first = initialOpenPaymentsCategory[0];
    // If it's already an object with id and name, use it directly
    if (typeof first === 'object' && first !== null && 'id' in first) {
      return initialOpenPaymentsCategory as OpenPaymentsCategoryItem[];
    }
    // If it's just an ID string, create a placeholder
    if (typeof first === 'string') {
      return [{ id: first, displayLabel: first, name: first }];
    }
    return [];
  };
  const [openPaymentsCategory, setOpenPaymentsCategory] =
    useState<OpenPaymentsCategoryItem[]>(getInitialCategories);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch] = useDebounce(searchTerm, 300);

  // Track if this is a new component from a placeholder that hasn't saved yet
  const [isNewFromPlaceholder, setIsNewFromPlaceholder] = useState(
    fromPlaceholder && (!initialOpenPaymentsCategory || initialOpenPaymentsCategory.length === 0)
  );

  // Fetch open payments category suggestions
  const { data, isLoading } =
    useGetOpenPaymentsCategorySuggestionsInfiniteQuery({
      q: debouncedSearch,
    });

  // Transform API results to standard format
  const searchResults: OpenPaymentsCategoryItem[] = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.results ?? []).map(transformOpenPaymentsCategory);
  }, [data]);

  // Auto-open modal if starting with no open payments category (new component)
  useEffect(() => {
    if (
      !initialOpenPaymentsCategory ||
      initialOpenPaymentsCategory.length === 0
    ) {
      setShowModal(true);
    }
  }, [initialOpenPaymentsCategory]);

  // Sync when external items are added (slash menu merge)
  useEffect(() => {
    const initialCategories = getInitialCategories();
    if (initialCategories.length > openPaymentsCategory.length) {
      setOpenPaymentsCategory(initialCategories);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOpenPaymentsCategory]);

  const handleSave = useCallback(
    (
      newOpenPaymentsCategory: OpenPaymentsCategoryItem[],
      connector: ConnectorType
    ) => {
      if (!newOpenPaymentsCategory || newOpenPaymentsCategory.length === 0) {
        onRemove(componentId);
        setShowModal(false);
        return;
      }
      const selectedOpenPayment = newOpenPaymentsCategory[0];
      setOpenPaymentsCategory(newOpenPaymentsCategory);

      // Clear the new-from-placeholder flag since we have data now
      setIsNewFromPlaceholder(false);

      onUpdate(componentId, {
        type: 'open_payments_category',
        value: selectedOpenPayment.id,
        ui_metadata: [selectedOpenPayment],
        connector: connector || 'ANY',
      });
      setShowModal(false);
    },
    [componentId, onRemove, onUpdate]
  );

  // Handle modal close - if from placeholder with no data, remove component
  const handleModalClose = useCallback(() => {
    setShowModal(false);
    // If this was new from a placeholder and user closed without selecting, remove it
    if (isNewFromPlaceholder && openPaymentsCategory.length === 0) {
      onRemove(componentId);
    }
  }, [isNewFromPlaceholder, openPaymentsCategory.length, onRemove, componentId]);

  const handleRemoveItem = useCallback(() => {
    onRemove(componentId);
  }, [componentId, onRemove]);

  return (
    <span data-testid="open-payments-category-component" className="inline">
      {openPaymentsCategory.length > 0 ? (
        <GenericNodeComponent
          config={openPaymentsCategoryNodeConfig}
          items={openPaymentsCategory}
          connector="ANY"
          onRemoveItem={handleRemoveItem}
          onAddMore={() => setShowModal(true)}
        />
      ) : (
        <button
          type="button"
          className="inline-flex items-center gap-1 px-2 py-1 bg-violet-50 border border-violet-200 rounded-full cursor-pointer hover:bg-violet-100 transition-colors"
          onClick={() => setShowModal(true)}
          data-testid="open-payments-category-select-trigger"
          aria-label="Select open payments category"
        >
          <span className="text-violet-600 text-xs font-medium">
            Select category...
          </span>
        </button>
      )}
      <GenericSelectorModal
        config={openPaymentsCategoryModalConfig}
        isOpen={showModal}
        selectedItems={openPaymentsCategory}
        connector="ANY"
        isEditing={openPaymentsCategory.length > 0}
        onClose={handleModalClose}
        onSave={handleSave}
        searchResults={searchResults}
        isLoading={isLoading}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
      />
    </span>
  );
};

export default OpenPaymentsCategoryComponent;

// Static method to render into a DOM element (for contentEditable integration)
export const renderOpenPaymentsCategoryComponent = (
  container: HTMLElement,
  props: OpenPaymentsCategoryComponentProps & {
    initialOpenPaymentsCategory?: OpenPaymentsCategoryItem[];
    initialConnector?: ConnectorType;
  }
) => {
  const root = createRoot(container);
  root.render(
    <Provider store={store}>
      <OpenPaymentsCategoryComponent {...props} />
    </Provider>
  );
  return root;
};
